import api from '../../api/client';
import React, { useState, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Button,
  Select,
  DatePicker,
  Modal,
  Form,
  Radio,
  Statistic,
  Steps,
  Table,
  Tag,
  Typography,
  message,
  Spin,
  Alert,
  Divider,
  Space,
  Tabs
} from "antd";
import {
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  BankOutlined,
  MobileOutlined,
  // CashOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import config from "../../config";
import { FaCashRegister } from "react-icons/fa";

const { Title, Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;
const { Step } = Steps;

const PayrollPaymentPage = () => {
  const [period, setPeriod] = useState(dayjs());
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [breakdown, setBreakdown] = useState(null);
  const accounts = useSelector((state) => state.account.user);
  const environment = window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;

  const userId = accounts?.id || "System";
  const userName = `${accounts?.firstname} ${accounts?.lastname}` || "System";

  // Fetch payment status and breakdown
  const fetchPaymentStatus = async () => {
    setLoading(true);
    try {
      const formattedPeriod = period.format("YYYY-MM");
      const { data } = await api.get(`/payroll/payment-status/${formattedPeriod}`);
      setPaymentStatus(data);

      // Fetch breakdown
      const breakdownRes = await api.get(`/hr/payroll/payslips`, {
        params: { period: formattedPeriod },
      });
      setBreakdown(breakdownRes.data);
    } catch (err) {
      message.error("Failed to fetch payment status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentStatus();
  }, [period]);

  const handleMarkApproved = async () => {
    Modal.confirm({
      title: "Confirm Approval",
      content: `Mark ${period.format("MMMM YYYY")} payroll as approved? This will process approval of payments to employees.`,
      onOk: async () => {
        setProcessing(true);
        try {
          const { data } = await api.post(`/hr/payroll/mark-approved`, {
            period: period.format("YYYY-MM"),
          });
          message.success(data.message || "Payroll approved successfully");
          fetchPaymentStatus();
        } catch (err) {
          message.error(err.response?.data?.message || "Failed to approve payroll");
        } finally {
          setProcessing(false);
        }
      },
    });
  };

  const handleProcessPayments = async () => {
    setShowPaymentModal(false);
    setProcessing(true);
    try {
      const { data } = await api.post(`/hr/payroll/process-payments`, {
        period: period.format("YYYY-MM"),
        paymentMethod,
        paidBy: userId,
        paidByName: userName,
      });
      message.success(data.message || "Payments processed successfully");
      fetchPaymentStatus();
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to process payments");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <Spin tip="Loading payment status..." />;
  if (!paymentStatus) return null;

  const paidEmployees = paymentStatus?.statusSummary?.find((s) => s.payment_status === "paid")?.count || 0;
  const pendingEmployees = paymentStatus?.statusSummary?.find((s) => s.payment_status === "pending")?.count || 0;
  const approvedEmployees = paymentStatus?.statusSummary?.find((s) => s.payment_status === "approved")?.count || 0;
  const totalEmployees = paidEmployees + pendingEmployees + approvedEmployees;
  const totalNet = paymentStatus?.statusSummary?.reduce((sum, item) => sum + (item.total_net || 0), 0);
  const totalGross = paymentStatus?.statusSummary?.reduce((sum, item) => sum + (item.total_gross || 0), 0);

  // Calculate deduction totals
  const totalPAYE = breakdown?.reduce((sum, item) => sum + parseFloat(item.paye || 0), 0) || 0;
  const totalNSSF = breakdown?.reduce((sum, item) => sum + parseFloat(item.nssf || 0), 0) || 0;
  const totalHousingLevy = breakdown?.reduce((sum, item) => sum + parseFloat(item.housing_levy || 0), 0) || 0;
  const totalOtherDeductions = breakdown?.reduce((sum, item) => sum + parseFloat(item.otherDeductions || 0), 0) || 0;
  const totalDeductions = breakdown?.reduce((sum, item) => sum + parseFloat(item.totalDeductions || 0), 0) || 0;

  // Group breakdown by department
  const departments = breakdown?.reduce((acc, item) => {
    const dept = item?.department || "Unassigned";
    if (!acc[dept]) acc[dept] = { net: 0, count: 0, gross: 0 };
    acc[dept].net += parseFloat(item.net || 0);
    acc[dept].gross += parseFloat(item.gross || 0);
    acc[dept].count++;
    return acc;
  }, {});

  const departmentColumns = [
    { title: "Department", dataIndex: "department", key: "department" },
    { title: "Employees", dataIndex: "count", key: "count", align: "center" },
    { title: "Gross Pay", dataIndex: "gross", key: "gross", align: "right", render: (v) => `KES ${v?.toLocaleString()}` },
    { title: "Net Pay", dataIndex: "net", key: "net", align: "right", render: (v) => `KES ${v?.toLocaleString()}` },
  ];

  const departmentData = Object.entries(departments || {}).map(([dept, data]) => ({
    department: dept,
    count: data.count,
    gross: data.gross,
    net: data.net,
  }));

  // Determine current step
  const getCurrentStep = () => {
    if (paidEmployees === totalEmployees && totalEmployees > 0) return 2;
    if (approvedEmployees > 0) return 1;
    return 0;
  };

  return (
    <>
      {/* Period Selector */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Text strong>Payroll Period:</Text>
          </Col>
          <Col span={8}>
            <DatePicker
              picker="month"
              value={period}
              onChange={(date) => date && setPeriod(date)}
              format="MMMM YYYY"
              style={{ width: "100%" }}
            />
          </Col>
          <Col span={6}>
            <Button type="primary" onClick={fetchPaymentStatus} loading={loading}>
              Refresh
            </Button>
          </Col>
        </Row>
      </Card>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Employee Payments Approval"
              value={`${approvedEmployees + paidEmployees}/${totalEmployees}`}
              prefix={<CheckCircleOutlined />}
            />
            {pendingEmployees > 0 && (
              <Alert
                message={`${period.format("MMMM YYYY")} payroll awaiting approval (KES ${totalNet?.toLocaleString()})`}
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
              />
            )}
            <Button
              type="primary"
              disabled={pendingEmployees === 0 || processing}
              onClick={handleMarkApproved}
              loading={processing}
              style={{ marginTop: 12 }}
              block
            >
              Approve Payments
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Employee Payments"
              value={`${paidEmployees}/${totalEmployees}`}
              prefix={<BankOutlined />}
            />
            {(pendingEmployees > 0 || approvedEmployees > 0) && (
              <Alert
                message={`${pendingEmployees || approvedEmployees} employees awaiting payment (KES ${totalNet?.toLocaleString()})`}
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
              />
            )}
            <Button
              type="primary"
              disabled={approvedEmployees === 0 || processing}
              onClick={() => setShowPaymentModal(true)}
              loading={processing}
              style={{ marginTop: 12 }}
              block
            >
              Process Payments
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Tax Obligations"
              value={paymentStatus?.taxPaid ? "Paid" : "Pending"}
              prefix={<DollarOutlined />}
              valueStyle={{ color: paymentStatus?.taxPaid ? "#52c41a" : "#faad14" }}
            />
            <Button
              type="default"
              disabled={paymentStatus?.taxPaid || paidEmployees === 0}
              onClick={async () => {
                Modal.confirm({
                  title: "Pay Taxes",
                  content: `Process tax payments for ${period.format("MMMM YYYY")}?`,
                  onOk: async () => {
                    try {
                      await api.post(`/payroll/pay-taxes`, { period: period.format("YYYY-MM") });
                      message.success("Taxes processed successfully");
                      fetchPaymentStatus();
                    } catch (err) {
                      message.error("Failed to process taxes");
                    }
                  },
                });
              }}
              style={{ marginTop: 12 }}
              block
            >
              Pay Taxes
            </Button>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic title="Total Gross Payroll" value={totalGross} prefix="KES " precision={2} />
            <Statistic title="Total Net Payments" value={totalNet} prefix="KES " precision={2} style={{ marginTop: 8 }} />
            <Divider style={{ margin: "12px 0" }} />
            <Statistic title="Total Deductions" value={totalGross - totalNet} prefix="KES " precision={2} valueStyle={{ color: "#cf1322" }} />
          </Card>
        </Col>
      </Row>

      {/* Accounting Flow Steps */}
      <Card style={{ marginBottom: 24 }} title="Accounting Flow">
        <Steps current={getCurrentStep()} size="small">
          <Step title="Payroll Generation" description="Calculate salaries" />
          <Step title="Employee Payments" description="Process payments" />
          <Step title="Tax Payments" description="Remit deductions" />
        </Steps>
      </Card>

      {/* Payment Distribution */}
      <Card title="Payment Distribution" style={{ marginBottom: 24 }}>
        <Tabs defaultActiveKey="departments">
          <TabPane tab="By Department" key="departments">
            <Table
              columns={departmentColumns}
              dataSource={departmentData}
              rowKey="department"
              pagination={false}
              size="small"
            />
          </TabPane>
          <TabPane tab="Deduction Breakdown" key="deductions">
            <Row gutter={16}>
              <Col span={12}>
                <Card size="small">
                  <Statistic title="PAYE" value={totalPAYE} prefix="KES " precision={2} />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic title="NSSF" value={totalNSSF} prefix="KES " precision={2} />
                </Card>
              </Col>
              <Col span={12} style={{ marginTop: 16 }}>
                <Card size="small">
                  <Statistic title="Housing Levy" value={totalHousingLevy} prefix="KES " precision={2} />
                </Card>
              </Col>
              <Col span={12} style={{ marginTop: 16 }}>
                <Card size="small">
                  <Statistic title="Other Deductions" value={totalOtherDeductions} prefix="KES " precision={2} />
                </Card>
              </Col>
            </Row>
            <Divider />
            <Card size="small" style={{ backgroundColor: "#f6ffed", borderColor: "#b7eb8f" }}>
              <Statistic
                title="Total Deductions"
                value={totalDeductions}
                prefix="KES "
                precision={2}
                valueStyle={{ color: "#cf1322" }}
              />
            </Card>
          </TabPane>
        </Tabs>
      </Card>

      {/* Payment Method Modal */}
      <Modal
        title="Process Salary Payments"
        open={showPaymentModal}
        onCancel={() => setShowPaymentModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowPaymentModal(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleProcessPayments} loading={processing}>
            Confirm Payments
          </Button>,
        ]}
      >
        <Alert
          message={`You are about to process payments for ${approvedEmployees} employees with a total amount of KES ${totalNet?.toLocaleString()}`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form layout="vertical">
          <Form.Item label="Select Payment Method" required>
            <Radio.Group value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
              <Radio value="bank_transfer">
                <BankOutlined /> Bank Transfer
              </Radio>
              <Radio value="mpesa">
                <MobileOutlined /> M-Pesa
              </Radio>
              <Radio value="cash">
                <FaCashRegister /> Cash
              </Radio>
              <Radio value="cheque">
                <CreditCardOutlined /> Cheque
              </Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default PayrollPaymentPage;